// Package imports:
import React, { useEffect, useState } from "react";
// Component imports:
import DisplayBox from "../../ui-elements/DisplayBox/DisplayBox";
import Table from "../../ui-elements/Table/Table";
import ErrorAlert from "../ErrorAlert/ErrorAlert";
import Loading from "../../ui-elements/Loading/Loading";
import HeadSection from "../../ui-elements/HeadSection/HeadSection";
import Button from "../../ui-elements/Button/Button";
import ForeignCompanyPepModal from "./ForeignCompanyPepModal";
import Link from "../../ui-elements/Link/Link";
// Service imports:
import { GET_KELDAN_API_URL } from "../../services/config";
import { getTableDate } from "../../services/utils";
import { ErrorMessages } from "../../services/errorMessages";
// Type imports:
import { Fetched } from "../../types/Types";
import { IKeldanApiResponse } from "../../types/KeldanTypes";
import { CompanyInitRequest, ForeignCompanyPep, PepCompanyResult } from "../../types/ForeignCompanyPepTypes";
import { formatCategories } from "./PepPage";

interface IfetchedData {
  eventDate: string;
  reply: string;
  searchTerm: string;
}

const ViewForeignCompanyReport: React.FC<{ id: string }> = ({ id }) => {
  const [showModal, setShowModal] = useState(false);
  const [modalData, setModalData] = useState<PepCompanyResult | undefined>(undefined);
  const [fetchedData, setFetchedData] = useState<Fetched<ForeignCompanyPep>>(null);
  const [searchTerm, setSearchTerm] = useState<CompanyInitRequest | null>(null);
  useEffect(() => {
    const fetchData = async () => {
      try {
        const url = `${GET_KELDAN_API_URL()}/PEP/GetForeignCompanyReport/${id}`;
        const response = await fetch(url);
        if (response.ok) {
          const responseBody: IKeldanApiResponse<IfetchedData> = await response.json();
          if (responseBody.result) {
            //parse the data and create the data object
            const parsedData: ForeignCompanyPep = JSON.parse(responseBody.result.reply);
            setFetchedData(parsedData);
            setSearchTerm(parsedData.companyRequest);
          } else if (responseBody.message) {
            setFetchedData(new Error(responseBody.message));
          } else {
            setFetchedData(new Error(ErrorMessages.OtherShort));
          }
        } else {
          setFetchedData(new Error(ErrorMessages.RequestFailed));
        }
      } catch (e) {
        setFetchedData(new Error(ErrorMessages.NetworkError));
      }
    };
    fetchData();
  }, []);

  const displayResult = (data: ForeignCompanyPep) => {
    // null indicates loading
    if (data === undefined) return <></>;
    if (data === null) return <Loading />;
    // Check if connected
    return (
      <>
        <div style={{ marginTop: "10px" }}>
          <Table
            tableSize="lg"
            data={data.match ?? []}
            showEmptyTableText
            columns={[
              {
                title: "Match Rate",
                renderCell: ({ match_rate }) => match_rate,
                textAlign: "left",
              },
              {
                title: "Nafn",
                renderCell: ({ companyName }) => companyName ?? "",
                textAlign: "left",
              },
              {
                title: "Land",
                renderCell: ({ locations }) => {
                  if (!locations || locations.length === 0) return "-";

                  const codes = new Set<string>();
                  for (const l of locations) {
                    const code = l?.countryCode ?? "";
                    if (code.length > 0)codes.add(code);
                  }

                  return Array.from(codes).join(", ");
                },
                textAlign: "left",
              },
              {
                title: "Lýsing",
                renderCell: ({ generalInfo }) => generalInfo?.businessDescription,
                textAlign: "left",
              },
              {
                title: "Flokkur/ar",
                renderCell: ({ categories }) => formatCategories(categories),
                textAlign: "left",
              },
              {
                title: "Ítarefni",
                renderCell: (item) => (
                  <Button
                    onClick={() => {
                      setModalData(item);
                      setShowModal(true);
                    }}
                    buttonType="secondary"
                    size="sm"
                  >
                    Skoða
                  </Button>
                ),
              },
            ]}
          />
        </div>
      </>
    );
  };
  function displayInitRequest(request: CompanyInitRequest | null) {
    return (
      <>
        <div className="info-col">
          <div className="info-item">
            <span className="title">Nafn</span>
            <span className="value" style={{ textTransform: "capitalize" }}>
              {request?.metadata?.name ?? "-"}
            </span>
          </div>
        </div>
        <div className="info-col">
          <div className="info-item">
            <span className="title">Land</span>
            <span className="value" style={{ textTransform: "capitalize" }}>
              {request?.metadata?.country_code ?? "-"}
            </span>
          </div>
        </div>
      </>
    );
  }

  const displayReport = () => {
    if (fetchedData === null) return <Loading />;
    if (fetchedData instanceof Error) return <ErrorAlert error={fetchedData} />;
    return (
      <>
        <div className="info-col-wrapper">{displayInitRequest(searchTerm)}</div>
        <DisplayBox>
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <div className="italic-text">
              Skýrsla sótt:{" "}
              {fetchedData.date && fetchedData.date.length > 0 && fetchedData.date[0].date
                ? getTableDate(fetchedData.date[0].date, "DD/MM/YYYY HH:MM:SS", ".")
                : "-"}
            </div>
            <Link targetBlank linkSize="15" url={`/Pdf/Althjodlegir-valistar-logadilar/${id}`} icon="pdf">
              Sækja skýrslu
            </Link>
          </div>
          <div className="main">{displayResult(fetchedData)}</div>
        </DisplayBox>
      </>
    );
  };

  return (
    <div className="main KCL_viewPep KCL_individual-profile assets-report">
      <div className="shell">
        <HeadSection title="Alþjóðlegir válistar" />
        {displayReport()}
      </div>
      <ForeignCompanyPepModal show={showModal} close={() => setShowModal(false)} data={modalData} />
    </div>
  );
};

export default ViewForeignCompanyReport;
